import { PropsWithChildren } from 'react';

import { Outlet } from 'react-router-dom';

import { Box, DrawerProvider, Stack, tokens } from '@cast/design-system';
import {
  AIAssistantSidebar,
  useIsOpsPilotEnabled,
} from '@cast/features-organization-shared-opspilot';

import { EscalationPrivilegeHeader } from 'components/sticky-headers';
import { TourProvider } from 'core/tour';
import { TourProvider as TourProviderV2 } from 'core/tour-v2/TourProvider';
import { OnboardingWizardButton } from 'features/onboarding-wizard';

import { useSentryRouteTagging } from '@oneclick/monitoring/useSentryRouteTracking';

import { Header } from './_components/Header';
import { Sidebar } from './_components/sidebar/Sidebar';
import { useEnterpriseOrganizationRouteGuard } from './_hooks/useEnterpriseOrganizationRouteGuard';
import { CONTENT_MAX_WIDTH_ULTRAWIDE } from './layoutConstants';

type AppLayoutProps = PropsWithChildren<{
  noSidebar?: boolean;
}>;

export const AppLayout = ({ children, noSidebar }: AppLayoutProps) => {
  useSentryRouteTagging();
  useEnterpriseOrganizationRouteGuard();
  const { opsPilotStatus } = useIsOpsPilotEnabled();

  const content = children ?? <Outlet />;

  return (
    <Stack
      sx={{
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: tokens.theme.bg.structural.page,
      }}
    >
      <EscalationPrivilegeHeader />

      <Header />

      <Stack direction="row" alignItems="stretch" flex={1} minHeight={0}>
        {!noSidebar && <Sidebar />}

        <Box
          flex={1}
          minWidth={0}
          component="main"
          sx={{
            backgroundColor: tokens.theme.bg.structural.page,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            flex={1}
            minHeight={0}
            sx={{
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              px={40}
              py={26}
              maxWidth={CONTENT_MAX_WIDTH_ULTRAWIDE}
              width="100%"
              mx="auto"
              sx={{
                flex: '1 0 auto',
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }}
            >
              <TourProviderV2>
                <TourProvider>
                  <DrawerProvider>{content}</DrawerProvider>
                </TourProvider>
              </TourProviderV2>
            </Box>
          </Box>
          {/* Slot for PageFooter portal — always visible at the bottom of the content area */}
          <Box id="portal-slot-page_footer" />
        </Box>
      </Stack>

      {opsPilotStatus === 'on' && <AIAssistantSidebar />}
      <OnboardingWizardButton />
    </Stack>
  );
};
